import { useCallback, useMemo, useState } from "react"
import { Gauge } from "lucide-react"
import { useUserRoles } from "../../common/hooks"
import { RequireRole } from "../../common/validation"
import { ALLOWED_ROLES_BY_ROUTE } from "../../common/constants"
import { PageHeader, If, EmptyState } from "../../common/ui"
import { useFetchLeadScoresQuery } from "../../redux/queries"
import { LeadScoreCategory, LeadScoreQueryParams } from "../../types"
import { getUserBrandId } from "../../common/utils"
import PaginationWrapper from "../../common/ui/pagination/wrapper"
import ScoreSummaryCards from "../../common/ui/lead-scoring/score-summary-cards"
import CategoryFilterBar from "../../common/ui/lead-scoring/category-filter-bar"
import LeadScoreList from "../../common/ui/lead-scoring/lead-score-list"

const PAGE_SIZE = 50

const LeadScoringPage = () => {
  const { user, isSuperAdmin, isBankAdmin, isLoanOfficer } = useUserRoles()
  const [activeCategory, setActiveCategory] =
    useState<LeadScoreCategory | null>(null)
  const [page, setPage] = useState(1)

  const baseParams = useMemo<LeadScoreQueryParams>(() => {
    const params: LeadScoreQueryParams = {}
    if (!isSuperAdmin && !isBankAdmin && isLoanOfficer && user?.id) {
      params.loid = user.id
    } else if (!isSuperAdmin && isBankAdmin && user?.id && user?.org) {
      const brandId =
        getUserBrandId(user.org, user.id) ??
        user.org?.brands?.[0]?.id ??
        user?.brand_id
      if (brandId) params.brand_id = brandId
    }
    return params
  }, [user, isSuperAdmin, isBankAdmin, isLoanOfficer])

  // Unfiltered query for global stats (summary cards)
  const { data: statsResponse } = useFetchLeadScoresQuery({
    ...baseParams,
    limit: 1
  })

  // Filtered + paginated query for the list
  const listParams = useMemo<LeadScoreQueryParams>(
    () => ({
      ...baseParams,
      ...(activeCategory ? { category: activeCategory } : {}),
      page,
      limit: PAGE_SIZE
    }),
    [baseParams, activeCategory, page]
  )
  const { data: listResponse, isLoading } = useFetchLeadScoresQuery(listParams)

  const scores = listResponse?.data ?? []
  const pagination = listResponse?.pagination
  const stats = statsResponse?.stats

  const handleCategoryChange = useCallback(
    (category: LeadScoreCategory | null) => {
      setActiveCategory(category)
      setPage(1)
    },
    []
  )

  // PaginationWrapper is 0-indexed
  const pageIndex = page - 1
  const totalPages = pagination?.total_pages ?? 1

  return (
    <RequireRole allowedRoles={ALLOWED_ROLES_BY_ROUTE["lead-scoring"]}>
      <div className="min-h-full p-6 bg-white">
        <PageHeader
          title={
            <span>
              Scoreboard{" "}
              <span className="text-sm font-normal text-neutral-400">
                (Preview)
              </span>
            </span>
          }
          hasSubtitle
          subtitle="AI-powered intent scoring for your leads"
        />

        <div className="mt-6 space-y-5">
          <ScoreSummaryCards
            stats={stats}
            activeCategory={activeCategory}
            onCategoryClick={handleCategoryChange}
          />

          <CategoryFilterBar
            activeCategory={activeCategory}
            onChange={handleCategoryChange}
          />

          <If
            condition={!isLoading && scores.length > 0}
            isLoading={isLoading}
            then={<LeadScoreList scores={scores} />}
            else={
              <EmptyState
                icon={Gauge}
                heading="No leads found"
                subHeading="Try adjusting your filters or check back later"
              />
            }
          />

          {totalPages > 1 && (
            <PaginationWrapper
              pageIndex={pageIndex}
              pageSize={PAGE_SIZE}
              rowCount={pagination?.total ?? 0}
              totalCount={totalPages}
              disabledPrev={page <= 1}
              disabledNext={page >= totalPages}
              onClickPrev={() => setPage(p => Math.max(1, p - 1))}
              onClickNext={() => setPage(p => Math.min(totalPages, p + 1))}
              setPageIndex={idx => setPage(idx + 1)}
            />
          )}
        </div>
      </div>
    </RequireRole>
  )
}

export default LeadScoringPage
